import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const MANAGEMENT_ERROR_MESSAGES = {
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  platform_admin_required: '플랫폼 관리자만 유니온을 관리할 수 있습니다.',
  platform_admin_scope_mismatch: '담당하는 커뮤니티 유형(갤러리/채널)의 유니온만 관리할 수 있습니다.',
  union_not_found: '유니온을 찾을 수 없습니다.',
  membership_not_found: '멤버 정보를 찾을 수 없습니다.',
  membership_not_active: '현재 활성 상태인 멤버가 아닙니다.',
  invalid_member_role: '지정할 수 없는 멤버 역할입니다.',
  leader_role_protected: '주딱 역할은 일반 역할 변경으로 수정하거나 제외할 수 없습니다.',
  target_already_leader: '선택한 계정이 이미 주딱입니다.',
  leadership_changed_retry: '주딱 정보가 변경되었습니다. 목록을 새로고침한 뒤 다시 시도해주세요.',
  union_leader_required: '이 유니온에는 현재 주딱이 없어 이전할 수 없습니다.',
  union_not_active: '현재 활성 상태인 유니온이 아닙니다.',
  game_profile_union_mismatch: '대상 계정의 게임 프로필 소속이 유니온과 일치하지 않아 지정할 수 없습니다.',
  community_type_required: '갤러리인지 채널인지 커뮤니티 유형을 선택해주세요.',
};

function requireSupabaseClient() {
  if (!hasSupabaseConfig || !supabase) {
    throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  }

  return supabase;
}

function createManagementError(error, fallbackMessage) {
  const rawMessage = String(error?.message || '');
  const matchedCode = Object.keys(MANAGEMENT_ERROR_MESSAGES).find((code) => rawMessage.includes(code));
  const managementError = new Error(
    MANAGEMENT_ERROR_MESSAGES[matchedCode] || fallbackMessage || '유니온 관리 요청을 처리하지 못했습니다.',
  );
  managementError.code = matchedCode || error?.code || 'unknown_error';
  return managementError;
}

export async function loadPlatformUnionMembers(unionId) {
  const normalizedUnionId = String(unionId || '').trim();
  if (!normalizedUnionId) return [];

  const client = requireSupabaseClient();
  const { data, error } = await client.rpc('platform_list_union_members', {
    target_union_id: normalizedUnionId,
  });

  if (error) throw createManagementError(error, '유니온 멤버 목록을 불러오지 못했습니다.');

  return (data || []).map((row) => ({
    membershipId: row.membership_id,
    nickname: row.nickname,
    avatarUrl: String(row.avatar_url || ''),
    role: row.role,
    joinedAt: row.joined_at,
    guildMatches: Boolean(row.guild_matches),
  }));
}

export async function setPlatformUnionMemberRole({ unionId, membershipId, role }) {
  const normalizedUnionId = String(unionId || '').trim();
  const normalizedMembershipId = String(membershipId || '').trim();
  const normalizedRole = String(role || '').trim();

  if (!normalizedUnionId) throw createManagementError({ message: 'union_not_found' });
  if (!normalizedMembershipId) throw createManagementError({ message: 'membership_not_found' });
  if (!['deputy', 'member'].includes(normalizedRole)) throw createManagementError({ message: 'invalid_member_role' });

  const client = requireSupabaseClient();
  const { data, error } = await client.rpc('platform_set_union_member_role', {
    target_union_id: normalizedUnionId,
    target_membership_id: normalizedMembershipId,
    new_role: normalizedRole,
  });

  if (error) throw createManagementError(error, '멤버 역할을 변경하지 못했습니다.');
  if (data !== normalizedRole) throw createManagementError(null, '멤버 역할 변경 결과를 확인하지 못했습니다.');

  return data;
}

export async function transferPlatformUnionLeadership({ unionId, membershipId }) {
  const normalizedUnionId = String(unionId || '').trim();
  const normalizedMembershipId = String(membershipId || '').trim();

  if (!normalizedUnionId) throw createManagementError({ message: 'union_not_found' });
  if (!normalizedMembershipId) throw createManagementError({ message: 'membership_not_found' });

  const client = requireSupabaseClient();
  const { data, error } = await client.rpc('platform_transfer_union_leadership', {
    target_union_id: normalizedUnionId,
    target_membership_id: normalizedMembershipId,
  });

  if (error) throw createManagementError(error, '주딱 권한을 이전하지 못했습니다.');
  if (!data?.ok || data?.new_leader_membership_id !== normalizedMembershipId) {
    throw createManagementError(null, '주딱 권한 이전 결과를 확인하지 못했습니다.');
  }

  return data;
}

export async function setPlatformUnionCommunityType({ unionId, communityType }) {
  const normalizedUnionId = String(unionId || '').trim();
  const normalizedCommunityType = String(communityType || '').trim();

  if (!normalizedUnionId) throw createManagementError({ message: 'union_not_found' });
  if (!['gallery', 'channel'].includes(normalizedCommunityType)) {
    throw createManagementError({ message: 'community_type_required' });
  }

  const client = requireSupabaseClient();
  const { data, error } = await client.rpc('platform_set_union_community_type', {
    target_union_id: normalizedUnionId,
    new_community_type: normalizedCommunityType,
  });

  if (error) throw createManagementError(error, '커뮤니티 유형을 변경하지 못했습니다.');
  if (data !== normalizedCommunityType) throw createManagementError(null, '커뮤니티 유형 변경 결과를 확인하지 못했습니다.');

  return data;
}
